import { Drawer } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowForwardIos, Close } from '@mui/icons-material';
import { KnowledgeCneterStyle } from './knowledgeCenter.styles';
import DOMPurify from 'dompurify';
import GlossaryComponent from '../knowledgeCenterV2/knowledgeCenter';
import { Regular, SemiBold, Bold } from '@src/apps/styles/common-style';
import WelcomeModalConnector from '../WelcomeModal/WelcomeModalConnector';
import { checkIsInternal, checkIsSimulationMode } from '@src/utility/createStorage';
import { getReleaseNotesService } from '@src/apps/Header/Main/Main.service';
import { StorageService } from '@src/utility/storage.service';
import { ANALYTICS_EVENTS } from '@src/apps/constants/constant';

export const KnowledgeCenterComponent = (props) => {
  const [Open, setOpen] = useState(() => Boolean(props?.isOpen));
  const [isWelcomeKC, setIsWelcomeKC] = useState(() => props?.metricData?.metricId === 'ALL');
  const [showUserGuide, setShowUserGuide] = useState(false);
  const wasKnowledgeCenterOpenRef = useRef(false);
  const hasTrackedKnowledgeCenterCloseRef = useRef(false);
  const [storageService] = useState(() => new StorageService());

  useEffect(() => {
    if (showUserGuide) {
      window.open(
        'https://uhgazure.sharepoint.com/teams/UHCInsights652/SitePages/UHC-Insights-User-Guide.aspx',
        '_blank',
        'noopener,noreferrer'
      );
      const resetTimer = window.setTimeout(() => setShowUserGuide(false), 0);

      return () => window.clearTimeout(resetTimer);
    }
  }, [showUserGuide]);

  const _sanitize = (dirty: string, options: object) => ({
    __html: DOMPurify.sanitize(dirty, { ...options }),
  });
  const renderMetricText = (text: string) => {
    if (!text?.includes('~')) return text;
    return text.split('~').reduce((acc: React.ReactNode[], part, index) => {
      if (index === 0) return [part];
      return [
        ...acc,
        <span key={index} style={{ position: 'relative', top: '6px' }}>
          ~
        </span>,
        part,
      ];
    }, []);
  };
  const closeKnowledgeCenter = () => {
    setOpen(false);
    props.onCloseKc();
    if (!hasTrackedKnowledgeCenterCloseRef.current) {
      storageService.setAppEventDataLayerForAdobeOnModalClose(ANALYTICS_EVENTS.KNOWLEDGE_CENTER);
      hasTrackedKnowledgeCenterCloseRef.current = true;
    }
  };
  const replaceUHCSnasWithEnterprise = (response) => {
    return response?.map((item) => {
      // Clone to avoid mutating Redux state directly
      const cloned = {
        ...item,
        MetricDefinition: item?.MetricDefinition
          ? { ...item.MetricDefinition }
          : item?.MetricDefinition,
      };
      if (cloned?.MetricDefinition?.Definition) {
        cloned.MetricDefinition.Definition = cloned.MetricDefinition.Definition.replace(
          /UHCSans-Medium/g,
          Regular
        );
        cloned.MetricDefinition.Definition = cloned.MetricDefinition.Definition.replace(
          /UHC Sans/g,
          Regular
        );
        cloned.MetricDefinition.Definition = cloned.MetricDefinition.Definition.replace(
          /UHCSans-SemiBold/g,
          SemiBold
        );
        cloned.MetricDefinition.Definition = cloned.MetricDefinition.Definition.replace(
          /UHCSans-Bold/g,
          Bold
        );
      }
      if (cloned?.MetricDefinition?.Formula) {
        cloned.MetricDefinition.Formula = cloned.MetricDefinition.Formula.replace(
          /UHCSans-Medium/g,
          Regular
        );
        cloned.MetricDefinition.Formula = cloned.MetricDefinition.Formula.replace(
          /UHC Sans/g,
          Regular
        );
        cloned.MetricDefinition.Formula = cloned.MetricDefinition.Formula.replace(
          /UHCSans-SemiBold/g,
          SemiBold
        );
        cloned.MetricDefinition.Formula = cloned.MetricDefinition.Formula.replace(
          /UHCSans-Bold/g,
          Bold
        );
      }
      return cloned;
    });
  };
  const updatedGlossaryList = useMemo(
    () => replaceUHCSnasWithEnterprise(props.glossaryList),
    [props.glossaryList]
  );

  useEffect(() => {
    const isKnowledgeCenterOpen = Boolean(props?.isOpen);
    if (isKnowledgeCenterOpen && !wasKnowledgeCenterOpenRef.current) {
      hasTrackedKnowledgeCenterCloseRef.current = false;
      storageService.setAppEventDataLayerForAdobeOnModalOpen(ANALYTICS_EVENTS.KNOWLEDGE_CENTER);
    }
    wasKnowledgeCenterOpenRef.current = isKnowledgeCenterOpen;
    const stateUpdateTimer = window.setTimeout(() => {
      setOpen(props?.isOpen);
      if (props?.metricData?.metricId === 'ALL') setIsWelcomeKC(true);
    }, 0);

    return () => window.clearTimeout(stateUpdateTimer);
  }, [props, storageService]);

  const [isWelcomeKCModel, setIsWelcomeKCModel] = useState(false);

  const handleMetricClick = async (list) => {
    if (list.MetricLink === 1) {
      setIsWelcomeKC(false);
    } else if (list.MetricLink === 2) {
      setIsWelcomeKCModel(true);
      setOpen(false);
    } else if (list.MetricLink === 3) {
      const param =
        !checkIsInternal() || checkIsSimulationMode()
          ? '/RELEASENOTESEXTERNAL'
          : '/RELEASENOTESINTERNAL';
      const response = await getReleaseNotesService(param);
      if (response) {
        const file = new Blob([response], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      }
    } else if (list.MetricLink === 4) {
      setShowUserGuide(true);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        PaperProps={{ style: { width: '418px', overflowX: 'hidden' } }}
        open={Open}
        onClose={closeKnowledgeCenter}
      >
        <KnowledgeCneterStyle>
          <div className="filter-div-scroll">
            <div className="sidemargin">
              <div className="h-3-title-filter">
                <div className="close-icon-style">
                  <div
                    className="icon-left-filter"
                    onClick={closeKnowledgeCenter}
                    data-testid="closeKc"
                  >
                    <Close style={{ fontSize: 24 }} />
                  </div>
                </div>
                <div className="filter-text-react">Knowledge Center</div>
              </div>

              {isWelcomeKC && props?.onboardingKCList?.length ? (
                <div className="KC-sidenav">
                  <div className="metric-border"></div>
                  {props?.onboardingKCList
                    // ?.filter((list) => list.MetricLink !== 4 || checkIsInternal())
                    ?.filter((list) => {
                      if (list.MetricLink === 4) {
                        return checkIsInternal() && !checkIsSimulationMode();
                      } else {
                        return true;
                      }
                    })
                    ?.map(
                      (list: { MetricLink: number; MetricName: string; MetricText: string }, i) => (
                        <div key={i}>
                          <div
                            className="metric-definition"
                            onClick={() => handleMetricClick(list)}
                          >
                            <div className="metric-definition-text">
                              {list?.MetricName}
                              <span className="right-arrow-text">
                                <ArrowForwardIos style={{ fontSize: '1rem' }} />
                              </span>
                            </div>
                          </div>
                          <div className="metric-definition-greytext">
                            {renderMetricText(list?.MetricText)}
                          </div>
                          <div className="metric-border"></div>
                        </div>
                      )
                    )}
                </div>
              ) : (
                <div className="content">
                  <GlossaryComponent
                    {...props.metricData}
                    glossaryList={updatedGlossaryList}
                    isPreviewKC={props.isPreviewKC}
                    toggleWelcomeKC={(flag) => setIsWelcomeKC(flag)}
                  ></GlossaryComponent>
                </div>
              )}

              <div className="footerSection"></div>
            </div>
          </div>
        </KnowledgeCneterStyle>
      </Drawer>
      <WelcomeModalConnector
        title={props?.title}
        toggle={isWelcomeKCModel}
        onToggle={setIsWelcomeKCModel}
      />
    </>
  );
};
